/**
 * Reports > Loyalty (/reports/loyalty).
 *
 * How much of what we sold went to loyalty members, and how many new members we
 * signed up, for the week to date or any chosen fiscal week, by store
 * and day. Replaces the QuickSight dashboards "WTD Loyalty Dashboard", "Weekly
 * Loyalty Performance Dashboard" and the Loyalty sheet of "OMNI Sales and Loyalty
 * Dashboard".
 *
 * This page is deliberately thin. The screen itself is the shared `Loyalty`
 * component in `@ampersandbrands/ui` (presentational, so The Sweet Spot can reuse
 * it); this file fetches through `useLoyalty`, keeps the filters in the URL so a
 * view is a shareable link, and adds the page chrome: header with the pin toggle,
 * the daily email control, and the Excel download.
 *
 * Comparison is the previous fiscal week by default, or the same days last year;
 * there is no budget, because plan data stopped 2026-05-02.
 *
 * RBAC: `reports.loyalty` sees the whole fleet. A store manager (holding
 * `stores.sweetspot`) reaches an own-store view, enforced server-side like OMNI
 * Performance, so the store picker is hidden when the api reports store scope.
 *
 * Resilience: wrapped in an ErrorBoundary and degrades to a quiet "not available
 * yet" card on 404/503 while the api ships.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loyalty as LoyaltyScreen, type LoyaltyFilters } from '@ampersandbrands/ui';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ReportSubscribeControl } from '@/components/ReportSubscribeControl';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { useRetailStores } from '@/lib/useRetailStores';
import { useReportPageState } from '@/lib/reportPageState';
import { isNotAvailable } from '@/lib/useStores';
import { LOYALTY_REPORT_TYPE } from '@/lib/useReportSubscriptions';
import {
  downloadLoyaltyXlsx,
  loyaltySubscriptionFilters,
  parseLoyaltyFilters,
  useLoyalty,
  writeLoyaltyFilters,
} from '@/lib/useLoyalty';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from '../stores/storesShared';

/** The accessSlug that grants this report (fleet view). */
const SLUG = 'reports.loyalty';

/**
 * Fleet through `reports.loyalty`, or an own-store view through
 * `stores.sweetspot` (the same dual audience as OMNI Performance). The api
 * enforces the real scope; this only decides whether to show the page.
 */
function canViewLoyalty(perms: string[] | undefined, superAdmin = false): boolean {
  if (hasAccessSlug(perms, SLUG, superAdmin)) return true;
  return !!perms?.includes('stores.sweetspot');
}

function LoyaltyInner() {
  const { user } = useSession();
  const canView = canViewLoyalty(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseLoyaltyFilters(searchParams), [searchParams]);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const setFilters = useCallback(
    (next: LoyaltyFilters) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          writeLoyaltyFilters(sp, next);
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const dataQ = useLoyalty(filters, canView);
  const storesQ = useRetailStores();
  // A store-scoped caller sees one store, so the picker would only mislead.
  const storeOptions = useMemo(
    () =>
      dataQ.report?.scope === 'store'
        ? []
        : [...storesQ.stores]
            .sort((a, b) => Number(a.store_no) - Number(b.store_no))
            .map((s) => ({ store_no: String(s.store_no), store_name: s.store_name ?? '' })),
    [storesQ.stores, dataQ.report?.scope],
  );

  const pageState = useMemo(
    () => [
      { label: 'Period', value: dataQ.report?.period.label ?? (filters.week || 'Week to date') },
      { label: 'Comparison', value: dataQ.report?.comparison?.label ?? 'None' },
      { label: 'Stores', value: filters.store_no.length > 0 ? filters.store_no.join(', ') : 'All stores' },
    ],
    [filters, dataQ.report],
  );
  useReportPageState(pageState);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadLoyaltyXlsx(filters);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="Loyalty" />;

  const header = (
    <StoresHeader
      eyebrow="Reports"
      title="Loyalty"
      subtitle="How much of our sales went to loyalty members and how many new members we signed up, for the week to date or any fiscal week. Refreshed every hour."
      actions={<ToolPinToggle toolSlug="loyalty" toolName="Loyalty" />}
    />
  );

  if (isNotAvailable(dataQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="Loyalty" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      {toast && (
        <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
          {toast}
        </p>
      )}
      <LoyaltyScreen
        report={dataQ.report}
        loading={dataQ.isLoading}
        error={dataQ.error ? <ErrorCard message={dataQ.error.message} /> : undefined}
        filters={filters}
        onFiltersChange={setFilters}
        storeOptions={storeOptions}
        onExport={() => void handleExport()}
        exporting={exporting}
        toolbarExtra={
          <ReportSubscribeControl
            reportType={LOYALTY_REPORT_TYPE}
            filters={loyaltySubscriptionFilters(filters)}
            subscribeHint="Get the week to date by email every morning, saving the store and comparison choices you have applied. The email always covers the week so far as of that morning (the whole of last week on a Sunday)."
          />
        }
      />
    </div>
  );
}

export function Loyalty() {
  return (
    <ErrorBoundary>
      <LoyaltyInner />
    </ErrorBoundary>
  );
}
